// This mixin contains code that is used for commercial configs
import Vue from "vue";
import moment from "moment";
import { _ } from "underscore";

// Mixins
import elecCommConfigMixin from "./elecCommConfig";
import gasCommConfigMixin from "./gasCommConfig";
import snotifyHttpError from "../../snotifyHttpResponses";

export default {
  mixins: [elecCommConfigMixin, gasCommConfigMixin, snotifyHttpError],
  data() {
    return {
      submitStatus: null,
    }
  },
  computed: {
    units() {
      if (this.activeServiceType === "Gas") return "AQ";
      else if (this.activeServiceType === "Electricity") return "EAC";
    }
  },
  methods: {
    saveConfigChanges(config) {
      this.submitStatus = "PENDING";

      config.CommercialPercentage = parseInt(config.CommercialPercentage);
      config.CcaReductionPercentage = parseInt(config.CcaReductionPercentage);
      config.EffectiveFrom = moment(config.EffectiveFrom).format("YYYY-MM-DD");

      let commercialType = null;
      // Gas specific params
      if (this.activeServiceType === "Gas") {
        commercialType = "/gas-commercials/";
        config.BillingAq = config.BillingAq;

        if (!this.overrideIndustry) {
          config.ApplicationRule = null
          config.BillingAq = null;
        }
      }
      // Electricity specific params
      else if (this.activeServiceType === "Electricity") {
        commercialType = "/elec-commercials/";
        config.EacLevel = config.EacLevel;

        if (this.overrideIndustry) {
          // Splits MeterPointBillingEac across all registers
          if (config.EacLevel === "MeterPoint") {
            this.updateBillingEacs(config);
          }

          // Enters an EAC total or manually entered EAC depending on EacLevel
          config.MeterPointBillingEac =
            config.EacLevel === "Register"
              ? this.getMeterPointBillingEac(config)
              : parseInt(config.MeterPointBillingEac);

          if (config.MeterPointBillingEac === null || undefined) {
            this.resetEacValues(config);
            this.overrideIndustry = null;
            // eslint-disable-next-line no-console
            console.warn('Override Industry Eac is disabled as MeterPointBillingEac is not provided.');
          }
        } else {
          this.resetEacValues(config);
        }
      }

      this.$snotify.async(
        "Saving Changes",
        () =>
          new Promise((resolve, reject) => {
            this.$http
              .put(
                "https://" +
                Vue.config.honeycomb.queenbee.domain +
                "/api/v1/contracts/" +
                this.activeContract.Id +
                "/services/" +
                this.activeService.ServiceIdentifier +
                commercialType +
                config.Id,
                config,
                {
                  withCredentials: true
                }
              )
              .then(response => {
                this.submitStatus = "OK";
                this.activeContract = response.data;

                if (Array.isArray(response.data.Services) && response.data.Services.length > 0) {
                  // eslint-disable-next-line no-console
                  console.log('Services array:', response.data.Services);
                  // eslint-disable-next-line no-console
                  console.log('Active Service Identifier:', this.activeService.ServiceIdentifier);

                  // Ensure activeService is properly initialized
                  if (this.activeService && this.activeService.ServiceIdentifier) {
                    this.activeService = response.data.Services.find(service =>
                      service.ServiceIdentifier == this.activeService.ServiceIdentifier
                    );

                    // Log the result to see if the correct service was found
                    // eslint-disable-next-line no-console
                    console.log('Found active service:', this.activeService);
                  } else {
                    // eslint-disable-next-line no-console
                    console.error('Active service identifier is missing or invalid:', this.activeService);
                  }
                } else {
                  // eslint-disable-next-line no-console
                  console.error('Services array is empty or invalid:', response.data.Services);
                }

                this.$store.dispatch("getContracts");
                this.activeCommercialConfig = config;
                this.editConfigMode = false;
                this.configCopy = null;

                resolve({
                  title: "Success!",
                  body: "Changes saved!",
                  config: {
                    closeOnClick: true,
                    timeout: 2000
                  }
                });
              })
              .catch(error => {
                this.submitStatus = "ERROR";
                // eslint-disable-next-line no-console
                console.log(error);
                reject(this.snotifyHttpError(error));
              }).finally(() => {
                this.$store.dispatch('refreshCustomer')
              });
          })
      );
    },

    resetEacValues(config) {
      config.ApplicationRule = null;
      config.BillingEac = null;
      config.EacLevel = null;
      config.MeterPointBillingEac = null;
    },

    addCommercialConfig(config) {
      this.submitStatus = "PENDING";

      let commercialType = null;

      config.CommercialPercentage = parseInt(config.CommercialPercentage);
      config.CcaReductionPercentage = parseInt(config.CcaReductionPercentage);
      config.EffectiveFrom = moment(config.EffectiveFrom).format("YYYY-MM-DD");

      // Gas specific params
      if (this.activeServiceType === "Gas") {
        commercialType = "/gas-commercials/";
        config.BillingAq = parseInt(config.BillingAq);
      }
      // Electricity specific params
      else if (this.activeServiceType === "Electricity") {
        commercialType = "/elec-commercials/";
        config.EacLevel = config.EacLevel;

        if (this.overrideIndustry) {
          // Splits MeterPointBillingEac across all registers
          if (config.EacLevel === "MeterPoint") {
            this.updateBillingEacs(config);
          }

          // Enters an EAC total or manually entered EAC depending on EacLevel
          config.MeterPointBillingEac = config.EacLevel === "Register"
              ? this.getMeterPointBillingEac(config)
              : parseInt(config.MeterPointBillingEac);
        } else {
          // Set EAC-related values to null if overrideIndustry is false
          config.ApplicationRule = null;
          config.BillingEac = null;
          config.EacLevel = null;
          config.MeterPointBillingEac = null;
        }
      }

      this.$snotify.async(
        "Saving Changes",
        () =>
          new Promise((resolve, reject) => {
            this.$http
              .post(
                "https://" +
                Vue.config.honeycomb.queenbee.domain +
                "/api/v1/contracts/" +
                this.activeContract.Id +
                "/services/" +
                this.activeService.ServiceIdentifier +
                commercialType,
                config,
                {
                  withCredentials: true
                }
              )
              .then(response => {
                this.submitStatus = "OK";
                if (this.activeServiceType === "Electricity") {
                  this.config = this.elecConfigTemplate;
                } else if (this.activeServiceType === "Gas") {
                  this.config = this.gasConfigTemplate;
                }
                // this.$snotify.success(`Success! Commercial configuration added.`);
                resolve({
                  title: "Success!",
                  body: "Commercial configuration added!",
                  config: {
                    closeOnClick: true,
                    timeout: 2000
                  }
                });
                this.$store.dispatch("getContracts");
                this.activeContract = response.data;
                this.activeService = _.find(response.data.Services,
                  service =>
                    service.ServiceIdentifier ==
                    this.activeService.ServiceIdentifier
                );
                document.getElementById("closeAddCommercialConfig").click();
              })
              .catch(error => {
                this.submitStatus = "ERROR";
                reject(this.snotifyHttpError(error));
              });
          })
      );
    },
  }
}